/**
 * useForex — Zustand store for the Forex Dashboard.
 * Fetches dashboard overview, pair detail, cross-rate matrix.
 *
 * Debounce: won't re-fetch if data was fetched less than 30s ago.
 * Stale data: keeps last known data on error / during background refresh.
 */
import { create } from 'zustand'
import { getForexDashboard, getForexPairDetail, getForexCrossRates } from '@/lib/api'
import type { ForexDashboard, ForexPairDetail, ForexCrossRates } from '@/lib/types'
import { useEffect } from 'react'

const DEBOUNCE_MS = 30_000

interface ForexState {
  dashboard: ForexDashboard | null
  pairDetail: ForexPairDetail | null
  crossRates: ForexCrossRates | null

  loading: boolean
  refreshing: boolean
  error: string | null
  pairLoading: boolean

  _lastFetchedAt: number

  fetchDashboard: () => Promise<void>
  fetchPairDetail: (pair: string) => Promise<void>
  fetchCrossRates: () => Promise<void>
  fetchAll: (force?: boolean) => Promise<void>
}

export const useForexStore = create<ForexState>()((set, get) => ({
  dashboard: null,
  pairDetail: null,
  crossRates: null,
  loading: false,
  refreshing: false,
  error: null,
  pairLoading: false,
  _lastFetchedAt: 0,

  fetchDashboard: async () => {
    set({ loading: true, error: null })
    try {
      const data = await getForexDashboard()
      set({ dashboard: data, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  fetchPairDetail: async (pair: string) => {
    set({ pairLoading: true })
    try {
      const data = await getForexPairDetail(pair)
      set({ pairDetail: data, pairLoading: false })
    } catch (e) {
      set({ pairLoading: false })
    }
  },

  fetchCrossRates: async () => {
    try {
      const data = await getForexCrossRates()
      set({ crossRates: data })
    } catch (_) { /* swallow */ }
  },

  fetchAll: async (force = false) => {
    const now = Date.now()
    const state = get()

    if (!force && state._lastFetchedAt > 0 && now - state._lastFetchedAt < DEBOUNCE_MS) {
      return
    }

    const hasData = state.dashboard !== null
    if (hasData) {
      set({ refreshing: true, error: null })
    } else {
      set({ loading: true, error: null })
    }

    try {
      const [dashboard, crossRates] = await Promise.all([
        getForexDashboard(),
        getForexCrossRates(),
      ])
      set({
        dashboard,
        crossRates,
        loading: false,
        refreshing: false,
        error: null,
        _lastFetchedAt: Date.now(),
      })
    } catch (e) {
      set({ error: String(e), loading: false, refreshing: false })
    }
  },
}))

/** Drop-in hook: fetches all forex data on mount, auto-refreshes every 30s. */
export function useForexWithRefresh(intervalMs = 30_000) {
  const store = useForexStore()

  useEffect(() => {
    store.fetchAll()
    const id = setInterval(() => store.fetchAll(), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs]) // eslint-disable-line

  return store
}
